/**
 * Renders `rows` as CSV, quoting every cell and escaping embedded quotes.
 *
 * Shared because the admin CSV surfaces each grew their own copy of the same
 * quote-and-join.
 */
export function toCsv(rows: readonly (readonly string[])[]): string {
  return rows
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

/** Quotes only when needed, and doubles embedded quotes per RFC 4180. */
export function escapeCsvValue(value: string): string {
  if (!/[",\n\r]/.test(value)) {
    return value;
  }
  return `"${value.replace(/"/g, '""')}"`;
}

/** Escapes every cell, so callers pass raw values and cannot forget one. */
export function buildRowsCsv(
  columns: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  const lines = [columns.map(escapeCsvValue).join(",")];
  for (const row of rows) {
    lines.push(row.map(escapeCsvValue).join(","));
  }
  return lines.join("\n");
}

export function downloadRowsCsv({
  columns,
  rows,
  filename,
}: {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
  filename: string;
}): void {
  downloadCsv(buildRowsCsv(columns, rows), filename);
}

/**
 * Prompts the browser to save `csv` as a file.
 *
 * Shared because every admin surface offering a CSV export or an example
 * template needs it, and each had grown its own near-identical copy.
 *
 * Set `bom` to prefix a UTF-8 byte-order mark, which Excel needs to read
 * accented characters and emoji rather than interpreting the bytes as a
 * single-byte encoding. It is off by default because a BOM corrupts the first
 * header for readers that do not strip it.
 */
export function downloadCsv(
  csv: string,
  filename: string,
  { bom = false }: { bom?: boolean } = {},
): void {
  const blob = new Blob([bom ? `\uFEFF${csv}` : csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;

  // The anchor has to be in the document before it is clicked: a detached one
  // does not reliably start a download in every browser.
  document.body.append(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
}
